import { useState, useCallback } from 'react';
import bookingService from '../../services/bookingService';

/**
 * Custom hook for JetSetter booking history management
 * @returns {Object} Booking history state and methods
 */
const useBookingHistory = () => {
  const [bookings, setBookings] = useState({
    upcoming: [],
    completed: [],
    cancelled: []
  });
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    limit: 10,
    totalPages: 0
  });
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const loadBookingHistory = useCallback(async (filters = {}) => {
    try {
      setLoading(true);
      setError(null);

      const status = typeof filters === 'string' ? filters : filters.status;
      const response = await bookingService.getBookingHistory(filters);

      if (response.success) {
        // Extract bookings array and pagination from response
        const { bookings: bookingsData = [], pagination: paginationData } = response.data || {};

        if (paginationData) {
          setPagination(paginationData);
        }

        setBookings(prev => {
          if (status) {
            return {
              ...prev,
              [status.toLowerCase()]: bookingsData
            };
          }

          // Fallback categorization if no status is provided
          const now = new Date();
          const categorized = { upcoming: [], completed: [], cancelled: [] };
          bookingsData.forEach(booking => {
            const departureDate = new Date(booking.departure_date_time || (booking.flights?.[0]?.departure_date_time));
            if (booking.booking_status === 'Cancelled') categorized.cancelled.push(booking);
            else if (booking.booking_status === 'Upcoming' || (departureDate > now && booking.booking_status === 'Confirmed')) categorized.upcoming.push(booking);
            else categorized.completed.push(booking);
          });
          return categorized;
        });
      } else {
        throw new Error(response.message || 'Failed to load booking history');
      }
    } catch (err) {
      console.error('Failed to load booking history:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  const getBookingDetails = useCallback(async (bookingId) => {
    try {
      setLoading(true);
      setError(null);

      const response = await bookingService.getBookingDetails(bookingId);

      if (response.success) {
        setSelectedBooking(response.data);
        return response.data;
      } else {
        throw new Error(response.message || 'Booking not found');
      }
    } catch (err) {
      console.error('Failed to get booking details:', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const getBookingByReference = useCallback(async (referenceNumber) => {
    try {
      setLoading(true);
      setError(null);

      const response = await bookingService.getBookingByReference(referenceNumber);

      if (response.success) {
        setSelectedBooking(response.data);
        return response.data;
      } else {
        throw new Error(response.message || 'Booking not found');
      }
    } catch (err) {
      console.error('Failed to get booking by reference:', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const cancelBooking = useCallback(async (bookingId, reason) => {
    try {
      setLoading(true);
      setError(null);

      const response = await bookingService.cancelBooking(bookingId, { reason });

      if (response.success) {
        // Reload booking history to reflect changes
        await loadBookingHistory();
        return response.data;
      } else {
        throw new Error(response.message || 'Failed to cancel booking');
      }
    } catch (err) {
      console.error('Failed to cancel booking:', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [loadBookingHistory]);

  const downloadTicket = useCallback(async (bookingId) => {
    try {
      setLoading(true);
      setError(null);

      // TODO: Implement actual ticket download from API
      console.log('Downloading ticket for booking:', bookingId);

      // Simulate download delay
      await new Promise(resolve => setTimeout(resolve, 1000));

      // For now, just show a message
      alert('Ticket download functionality will be implemented soon');
    } catch (err) {
      console.error('Failed to download ticket:', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const downloadInvoice = useCallback(async (bookingId) => {
    try {
      setLoading(true);
      setError(null);

      // TODO: Implement actual invoice download from API
      console.log('Downloading invoice for booking:', bookingId);

      // Simulate download delay
      await new Promise(resolve => setTimeout(resolve, 1000));

      // For now, just show a message
      alert('Invoice download functionality will be implemented soon');
    } catch (err) {
      console.error('Failed to download invoice:', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    bookings,
    pagination,
    selectedBooking,
    loading,
    error,
    loadBookingHistory,
    getBookingDetails,
    getBookingByReference,
    cancelBooking,
    downloadTicket,
    downloadInvoice,
    setSelectedBooking
  };
};

export default useBookingHistory;